import React, { useState, useRef, type JSX } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { TextFieldProps } from '@mui/material/TextField';
import { DateTimePicker, DateTimePickerProps } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import moment, { Moment } from 'moment';
import { Datum } from '../../types';
import { toDatum } from '../../shared/dateTime';

const testId = 'data-testid';

export type CustomDateTimePickerProps = {
  [testId]?: string;
  value?: Datum;
  onChange: (date: Date | null) => void;
} & Omit<TextFieldProps, 'onChange'> &
  Omit<DateTimePickerProps<Moment>, 'onChange' | 'value'>;

export function CustomDateTimePicker(props: CustomDateTimePickerProps): JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="de">
      <DateTimePicker
        {...props}
        enableAccessibleFieldDOMStructure={false}
        value={props.value ? moment(props.value) : null}
        viewRenderers={{
          hours: renderTimeViewClock,
          minutes: renderTimeViewClock,
          seconds: renderTimeViewClock
        }}
        onChange={(date: Moment | null) => {
          props.onChange(date ? moment(date).toDate() : null);
        }}
        ampm={false}
        open={open}
        onClose={() => {
          setOpen(false);
          if (props.onClose) {
            props.onClose();
          }
        }}
        slotProps={{
          textField: {
            onClick: () => !props.disableOpenPicker && setOpen(true),
            onBlur: props.onBlur,
            variant: 'standard',
            fullWidth: props.fullWidth,
            ...(props[testId] ? { 'data-testid': props[testId] } : {})
          } as Record<string, unknown>,
          field: {
            clearable: false
          }
        }}
      />
    </LocalizationProvider>
  );
}

export type CustomDatePickerProps = {
  selectEndOfMonth?: boolean;
  onDateSelect?: (value: Datum) => void;
  value?: Datum;
  [testId]?: string;
} & Omit<TextFieldProps, 'onChange'> &
  Omit<DatePickerProps<Moment>, 'onChange' | 'value' | 'minDate' | 'maxDate'> & {
  minDate?: Datum;
  maxDate?: Datum;
};

export function CustomDatePicker(props: CustomDatePickerProps): JSX.Element {
  const open = useRef(false);

  const [hasError, setHasError] = useState(false);

  function format(date: string | null) {
    return props.selectEndOfMonth ? toDatum(moment(date).endOf('month')) : toDatum(moment(date));
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="de">
      <DatePicker
        onOpen={() => {
          open.current = true;
        }}
        {...props}
        enableAccessibleFieldDOMStructure={false}
        value={props.value ? moment(props.value) : null}
        minDate={props.minDate ? moment(props.minDate) : undefined}
        maxDate={props.maxDate ? moment(props.maxDate) : undefined}
        onChange={(event: Moment | null) => {
          setHasError(false);
          if (!event || !event.isValid() || event.year() < 1900) return;
          const datum = format(event.toISOString());

          if (props.onDateSelect && datum && !open.current) {
            props.onDateSelect(datum);
          }
        }}
        onAccept={(event: Moment | null) => {
          setHasError(false);

          if (props.onDateSelect) {
            props.onDateSelect(format(event ? event.toISOString() : null));
          }
        }}
        onClose={() => {
          open.current = false;
        }}
        closeOnSelect={true}
        {...(props.views?.length === 2 && props.views?.includes('year') && props.views?.includes('month')
          ? { format: 'MM.YYYY' }
          : {})}
        slotProps={{
          textField: {
            onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
              setHasError(!isValidDateString(event.target.value));
            },
            error: hasError || Boolean(props.error),
            helperText: props.helperText,
            required: props.required,
            variant: 'standard',
            fullWidth: props.fullWidth,
            ...(props[testId] ? { 'data-testid': props[testId] } : {})
          } as Record<string, unknown>,
          field: {
            clearable: false
          }
        }}
      />
    </LocalizationProvider>
  );
}

function isValidDateString(value: string): boolean {
  if (value === '') {
    return true;
  }
  return /^(\d{2}\.)?\d{2}\.\d{4}$/.test(value);
}
